<template>
  <v-card
    class="mx-auto"
    color="white"
    max-width="500"
  >
    <v-toolbar
      flat
      color="transparent"
    >
      <v-toolbar-title>{{ title }}
        <v-toolbar-title
          v-if="subtitle"
          class="text-subtitle-2"
        >{{ subtitle }}</v-toolbar-title>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        icon
        @click="$refs.search.focus()"
      >
        <v-icon>mdi-magnify</v-icon>
      </v-btn>
    </v-toolbar>

    <slot name="toolbar">
      <!-- Put an additional toolbar in here if needed -->
    </slot>

    <!-- <v-card-text> -->

    <v-container class="py-0">
      <v-row
        align="center"
        justify="start"
      >
        <v-col
          v-for="item in selectedItems"
          :key="item[itemValue]"
          class="shrink"
        >
          <v-chip
            :disabled="loading"
            close
            @click:close="removeItem(item)"
          >
            <!-- <v-icon
              left
              v-text="selection.icon"
            ></v-icon> -->
            <strong>{{ item[itemValue] }}</strong>
            <span>({{ item[itemText] }})</span>
          </v-chip>
        </v-col>

        <v-col cols="12">
          <v-text-field
            ref="search"
            v-model="search"
            full-width
            hide-details
            label="Search"
            single-line
            clearable
            filled
          ></v-text-field>
        </v-col>
      </v-row>
    </v-container>

    <v-divider></v-divider>

    <v-list >
      <template v-for="(item, i) in searchResults">
        <v-list-item
          v-if="!isSelected(item)"
          :key="i"
          :disabled="loading"
          @click="addItem(item)"
        >
         <v-list-item-title>
            <strong>{{ item[itemValue] }}</strong>
            <span>({{ item[itemText] }})</span>
          </v-list-item-title>
        </v-list-item>
      </template>
      <template v-if="searchResults.length === 0">
        <v-list-item>
          <v-list-item-title>
            <strong>No Results</strong>
          </v-list-item-title>
        </v-list-item>
      </template>
    </v-list>

    <v-divider></v-divider>
    <v-card-actions align="end">
      <v-spacer></v-spacer>
      <v-btn
        v-if="extendedSearchFn"
        :loading="loading"
        color="orange"
        text
        @click="toggleExtendedSearch"
      >Use {{ useExtendedSearchFn ? "Default" : "Extended" }} Search</v-btn>
      <v-btn
        :disabled="!value.length"
        :loading="loading"
        color="red"
        text
        @click="clearSelected"
      >Clear Selected</v-btn>
    </v-card-actions>
    <v-divider></v-divider>
    <v-card-text v-if="allowManualAdd">
      <v-text-field v-model="manualItemValue" :placeholder="manualAddValuePlaceholder"></v-text-field>
      <v-text-field v-model="manualItemText" :placeholder="manualAddTextPlaceholder"></v-text-field>
      <v-btn @click="manualAddItem">Add Manual or Wildcard Filter</v-btn>
      <v-spacer></v-spacer>
      <p v-show="manualItemErrorMessage" class="red--text">{{ manualItemErrorMessage }}</p>
    </v-card-text>
  </v-card>
</template>

<script>

export default {
  name: 'custom-search',
  props: {
    title: { required: true, type: String },
    subtitle: { required: false, type: String },
    value: {
      type: Array,
      required: true,
      default: () => []
    },
    /**
     * Provide a function (promise based) that take one arg (searchTerm)
     * and returns the results in a list [{...}, {...}]
     */
    searchFn: {
      type: Function,
      required: true
    },
    extendedSearchFn: {
      type: Function,
      required: false
    },
    itemText: {
      type: String,
      required: false,
      default: 'text'
    },
    itemValue: {
      type: String,
      required: false,
      default: 'value'
    },
    allowManualAdd: {
      type: Boolean,
      required: false,
      default: false
    },
    manualAddTextPlaceholder: {
      type: String,
      required: false
    },
    manualAddValuePlaceholder: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      searchResults: [],
      loading: false,
      search: '',
      useExtendedSearchFn: false,
      manualItemText: null,
      manualItemValue: null,
      manualItemErrorMessage: null
    }
  },

  computed: {
    selectedItems () {
      const selections = []

      for (const selection of this.value) {
        selections.push(selection)
      }

      return selections
    },
    dynamicSearchFn () {
      return this.useExtendedSearchFn ? this.extendedSearchFn : this.searchFn
    }
  },

  watch: {
    search (newTerm) {
      if (!newTerm) {
        this.searchResults = []
        return
      }
      // only start search for 3 or more characters
      if (newTerm.length < 3) {
        return
      }
      this.$logger.trace('NEW SEARCH TERM', newTerm)
      this.dynamicSearchFn(newTerm)
        .then(searchResults => {
          this.searchResults = searchResults
        })
        .catch(error => {
          this.$logger.error('Error Loading Search Function Data', error.message)
          this.$flash({
            showClose: true,
            message: 'Searching. Reload to try again',
            type: 'error',
            duration: 0
          })
        })
    }
  },

  methods: {
    isSelected (item) {
      return this.value.map(item => item[this.itemValue]).includes(item[this.itemValue])
    },
    updateItems (newItems) {
      this.$emit('input', newItems)
    },
    removeItem (item) {
      const newModelVals = this.value.filter(existingItem => item[this.itemValue] !== existingItem[this.itemValue])
      this.updateItems(newModelVals)
    },
    addItem (item) {
      this.updateItems([item].concat(this.value))
    },
    manualAddItem () {
      if (this.manualItemText && this.manualItemValue) {
        this.addItem({ [this.itemValue]: this.manualItemValue, [this.itemText]: `MANUAL ${this.manualItemText}` })
        this.manualItemValue = this.manualItemText = this.manualItemErrorMessage = null
      } else {
        this.manualItemErrorMessage = 'You must enter both values to add manually'
      }
    },
    clearSelected () {
      this.search = ''
      this.loading = false
      this.updateItems([]) // reset to empty
    },
    toggleExtendedSearch () {
      this.useExtendedSearchFn = !this.useExtendedSearchFn
    }
  }
}
</script>

<style>
.searchResults {
  min-height: 200px;
  overflow-y: auto;
}
</style>
